import React, { useMemo, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import { Row, Col, Button } from 'reactstrap';
import Sidebar from '../../Sidebar';
import { useNavigate } from 'react-router';
import { useCookies } from 'react-cookie';
import EditSharpIcon from '@mui/icons-material/EditSharp';
import { displayError } from '../../helpers';
import ErrorToast from '../../ErrorToast';
import FloatingControls from '../../components/FloatingControls';
import LoadingOverlay from '../../components/LoadingOverlay';
import PremiumPageHeader from '../../components/PremiumPageHeader';
import ReactTablePopUpSubmitWrapper from '../../components/ReactTablePopUpSubmitWrapper';
import QuestionIcon from '../../components/QuestionIcon';
import { getUserAllowedPlants } from '../../services/PlantServices';
import { getAllVendors } from '../../services/VendorServices';
import { getAllTax } from '../../services/TaxServices';
import { getAllProducts } from '../../services/ProductServices';
import {
    createPurchaseInvoice,
    getGrnsForPurchaseInvoice,
    getPurchaseInvoiceNumber,
    uploadPendingPurchaseInvoiceDocuments,
} from '../../services/PurchaseInvoiceServices';
import { getCurrentDate, getCurrentTime, getDisplayDate } from '../../utils/DateUtils';
import PiUploadCentreModal from './PiUploadCentreModal';
import { revokePendingGrnDocument } from '../store/grnDocumentUtils';
import { formatAmount, formatQty } from '../../utils/listSummaryUtils';

function PurchaseInvoice() {
    const [cookies] = useCookies(['myToken']);
    const navigate = useNavigate();
    const [inProgress, setInProgress] = useState(false);
    const [plants, setPlants] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [taxes, setTaxes] = useState([]);
    const [products, setProducts] = useState([]);
    const [inputs, setInputs] = useState({
        invoice_mode: 'with_grn',
        invoice_date: getCurrentDate(),
        invoice_time: getCurrentTime(),
        plant_id: '',
        vendor_id: '',
        remarks: '',
        freight_charges: 0,
        discount_amt: 0,
        tcs_amt: 0,
        rounded_off: 0,
    });
    const [selectedGrns, setSelectedGrns] = useState([]);
    const [rowsData, setRowsData] = useState([]);
    const [showGrnModal, setShowGrnModal] = useState(false);
    const [grnPickerData, setGrnPickerData] = useState([]);
    const [isGrnLoading, setIsGrnLoading] = useState(false);
    const [showGrnDetailsModal, setShowGrnDetailsModal] = useState(false);
    const [grnDetailsRows, setGrnDetailsRows] = useState([]);
    const [grnDetailsTitle, setGrnDetailsTitle] = useState('List of GRN');
    const [grnDetailsProductKey, setGrnDetailsProductKey] = useState('');
    const [showUploadCentre, setShowUploadCentre] = useState(false);
    const [documents, setDocuments] = useState([]);
    const documentsRef = useRef([]);
    documentsRef.current = documents;

    React.useEffect(() => {
        return () => {
            documentsRef.current.forEach(revokePendingGrnDocument);
        };
    }, []);

    React.useEffect(() => {
        getUserAllowedPlants(cookies).then((plantList) => {
            setPlants(
                (plantList.plant_list || [])
                    .filter((obj) => obj.status)
                    .map((plant) => ({ value: plant.id, label: plant.plant_alias })),
            );
        });
        getAllVendors(cookies).then((vendorList) => {
            const list = vendorList.vendor_list || vendorList.entity_list || [];
            setVendors(
                list
                    .filter((obj) => obj.status !== false && obj.status !== 0)
                    .map((v) => ({ value: v.id, label: v.name || v.entity_name })),
            );
        }).catch(() => {});
        getAllTax(cookies).then((taxList) => {
            const list = taxList.tax_list || [];
            setTaxes(list.filter((t) => t.status).map((t) => ({
                value: t.id,
                label: `${t.name} (${t.tax_percentage || 0}%)`,
                perc: Number(t.tax_percentage) || 0,
            })));
        }).catch(() => {});
        getAllProducts(cookies).then((productList) => {
            const list = productList.product_list || [];
            setProducts(list.filter((p) => p.status).map((p) => ({
                value: p.id,
                label: p.name,
                unit: p.unit?.symbol || '',
            })));
        }).catch(() => {});
    }, [cookies]);

    React.useEffect(() => {
        if (!inputs.plant_id || !inputs.invoice_date) {
            return;
        }
        getPurchaseInvoiceNumber(cookies, inputs.plant_id, getDisplayDate(inputs.invoice_date))
            .then((res) => {
                setInputs((values) => ({
                    ...values,
                    invoice_no: res.invoice_no,
                    prefix: res.prefix,
                }));
            })
            .catch(() => {});
    }, [cookies, inputs.plant_id, inputs.invoice_date]);

    const isWithGrn = inputs.invoice_mode === 'with_grn';

    const calcTaxBreakdown = (taxable, taxPerc, existing = {}) => {
        const perc = Number(taxPerc) || 0;
        const isIgst = Number(existing.igst_perc) > 0;
        let cgst = Number(existing.cgst_amt) || 0;
        let sgst = Number(existing.sgst_amt) || 0;
        let igst = isIgst ? (Number(existing.igst_amt) || 0) : 0;
        let gst = isIgst ? igst : (cgst + sgst);
        if (gst <= 0) {
            gst = Number(((Number(taxable) || 0) * perc / 100).toFixed(2));
            if (isIgst) {
                igst = gst;
                cgst = 0;
                sgst = 0;
            } else {
                cgst = Number((gst / 2).toFixed(2));
                sgst = Number((gst - cgst).toFixed(2));
                igst = 0;
            }
        } else if (!isIgst) {
            igst = 0;
            gst = Number((cgst + sgst).toFixed(2));
            if (gst <= 0) {
                gst = Number(((Number(taxable) || 0) * perc / 100).toFixed(2));
                cgst = Number((gst / 2).toFixed(2));
                sgst = Number((gst - cgst).toFixed(2));
            }
        }
        const gstAmt = Number((cgst + sgst + igst).toFixed(2));
        const taxName = (existing.tax_name || '').trim();
        const taxLabel = taxName && /@\s*\d/.test(taxName)
            ? taxName
            : (taxName ? `${taxName} @ ${perc}%` : `GST @ ${perc}%`);
        return {
            tax_perc: perc,
            tax_name: taxName,
            tax_label: taxLabel,
            cgst_amt: cgst,
            sgst_amt: sgst,
            igst_amt: igst,
            gst_amt: gstAmt,
            gross_amt: Number(((Number(taxable) || 0) + gstAmt).toFixed(2)),
        };
    };

    const totals = useMemo(() => {
        const taxable = rowsData.reduce((sum, row) => sum + (Number(row.taxable_amt) || 0), 0);
        const gst = rowsData.reduce((sum, row) => sum + (Number(row.gst_amt) || 0), 0);
        const cgst = rowsData.reduce((sum, row) => sum + (Number(row.cgst_amt) || 0), 0);
        const sgst = rowsData.reduce((sum, row) => sum + (Number(row.sgst_amt) || 0), 0);
        const igst = rowsData.reduce((sum, row) => sum + (Number(row.igst_amt) || 0), 0);
        const gross = rowsData.reduce((sum, row) => sum + (Number(row.gross_amt) || 0), 0);
        const qty = rowsData.reduce((sum, row) => sum + (Number(row.invoice_qty) || 0), 0);
        const freight = Number(inputs.freight_charges) || 0;
        const discount = Number(inputs.discount_amt) || 0;
        const tcs = Number(inputs.tcs_amt) || 0;
        const baseAmount = Number((taxable + gst + freight + tcs - discount).toFixed(2));
        const invoiceAmt = Math.round(baseAmount);
        const roundedOff = Number((invoiceAmt - baseAmount).toFixed(2));
        return {
            taxable: taxable.toFixed(2),
            gst: gst.toFixed(2),
            cgst: cgst.toFixed(2),
            sgst: sgst.toFixed(2),
            igst: igst.toFixed(2),
            gross: gross.toFixed(2),
            qty: qty.toFixed(2),
            rounded_off: roundedOff.toFixed(2),
            invoice_amt: invoiceAmt.toFixed(2),
        };
    }, [rowsData, inputs.freight_charges, inputs.discount_amt, inputs.tcs_amt]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setInputs((values) => ({ ...values, [name]: value }));
        if (name === 'invoice_mode') {
            setSelectedGrns([]);
            setRowsData([]);
        }
        if (name === 'vendor_id' || name === 'plant_id') {
            setSelectedGrns([]);
            if (isWithGrn || name === 'vendor_id') {
                setRowsData([]);
            }
        }
    };

    const buildRowsFromGrns = (grns) => {
        const grouped = new Map();
        (grns || []).forEach((grn) => {
            (grn.detail_list || []).forEach((line) => {
                const productId = line.product?.id;
                if (!productId) {
                    return;
                }
                const tax = taxes.find((t) => String(t.value) === String(line.tax_id));
                const taxId = line.tax_id || tax?.value || '';
                const groupKey = `${productId}|${taxId}`;
                const qty = Number(line.accepted_qty || line.received_qty || 0);
                const rate = Number(line.basic_rate || 0);
                const taxable = Number(Number(line.taxable_amt || qty * rate).toFixed(2));
                const taxPerc = Number(line.tax_perc) || tax?.perc || 0;
                const taxNameRaw = line.tax_name || tax?.label?.split(' (')[0] || '';
                const breakdown = calcTaxBreakdown(taxable, taxPerc, {
                    ...line,
                    tax_name: taxNameRaw,
                });
                const grnDetail = {
                    id: `${grn.id}-${line.id}`,
                    grn_id: grn.id,
                    grn_no: grn.goods_receipt_note_no || '',
                    grn_date: grn.goods_receipt_note_date || '',
                    dc_no: grn.dc_no || '',
                    vehicle_no: grn.vehicle_no || '',
                    unit: line.product?.unit?.symbol || '',
                    dc_qty: Number(line.dc_qty || 0),
                    accepted_qty: qty,
                    basic_rate: rate,
                    taxable_amt: taxable,
                };
                const existing = grouped.get(groupKey);
                if (!existing) {
                    grouped.set(groupKey, {
                        key: groupKey,
                        grn_ids: [grn.id],
                        grn_nos: [grn.goods_receipt_note_no].filter(Boolean),
                        grn_details: [grnDetail],
                        product_id: productId,
                        product_name: line.product?.name || '',
                        unit: line.product?.unit?.symbol || '',
                        dc_qty: Number(line.dc_qty || 0),
                        invoice_qty: qty,
                        taxable_amt: taxable,
                        tax_id: taxId,
                        po_detail_id: line.po_detail_id,
                        tax_perc: taxPerc,
                        tax_name: breakdown.tax_name,
                        tax_label: breakdown.tax_label,
                        cgst_amt: breakdown.cgst_amt,
                        sgst_amt: breakdown.sgst_amt,
                        igst_amt: breakdown.igst_amt,
                        gst_amt: breakdown.gst_amt,
                        gross_amt: breakdown.gross_amt,
                        rate_amount_sum: rate * qty,
                    });
                    return;
                }
                if (!existing.grn_ids.includes(grn.id)) {
                    existing.grn_ids.push(grn.id);
                }
                if (grn.goods_receipt_note_no && !existing.grn_nos.includes(grn.goods_receipt_note_no)) {
                    existing.grn_nos.push(grn.goods_receipt_note_no);
                }
                existing.grn_details.push(grnDetail);
                existing.dc_qty = Number((existing.dc_qty + Number(line.dc_qty || 0)).toFixed(3));
                existing.invoice_qty = Number((existing.invoice_qty + qty).toFixed(3));
                existing.taxable_amt = Number((existing.taxable_amt + taxable).toFixed(2));
                existing.cgst_amt = Number((existing.cgst_amt + breakdown.cgst_amt).toFixed(2));
                existing.sgst_amt = Number((existing.sgst_amt + breakdown.sgst_amt).toFixed(2));
                existing.igst_amt = Number((existing.igst_amt + breakdown.igst_amt).toFixed(2));
                existing.gst_amt = Number((existing.gst_amt + breakdown.gst_amt).toFixed(2));
                existing.gross_amt = Number((existing.gross_amt + breakdown.gross_amt).toFixed(2));
                existing.rate_amount_sum += rate * qty;
                if (!existing.po_detail_id && line.po_detail_id) {
                    existing.po_detail_id = line.po_detail_id;
                }
            });
        });

        return Array.from(grouped.values()).map((row) => {
            const qty = Number(row.invoice_qty) || 0;
            const basicRate = qty > 0
                ? Number((row.rate_amount_sum / qty).toFixed(2))
                : 0;
            return {
                ...row,
                grn_id: row.grn_ids[0],
                grn_no: row.grn_nos.join(', '),
                basic_rate: basicRate,
            };
        });
    };

    const mapGrnDetailRow = (item) => ({
        ...item,
        isChecked: item.isChecked !== false,
        dc_qty_display: formatQty(item.dc_qty),
        accepted_qty_display: formatQty(item.accepted_qty),
        basic_rate_display: formatAmount(item.basic_rate),
        taxable_amt_display: formatAmount(item.taxable_amt),
    });

    const showGrnDetails = (row) => {
        if (!isWithGrn) {
            return;
        }
        const details = row.grn_details || [];
        setGrnDetailsProductKey(row.key || `${row.product_id}|${row.tax_id || ''}`);
        setGrnDetailsTitle(`List of GRN — ${row.product_name || 'Product'}`);
        setGrnDetailsRows(details.map((item) => mapGrnDetailRow({
            ...item,
            isChecked: item.isChecked !== false,
        })));
        setShowGrnDetailsModal(true);
    };

    const refreshGrnDetailsModal = (productKey, nextRows) => {
        const productRow = (nextRows || []).find((row) => row.key === productKey);
        if (!productRow) {
            setShowGrnDetailsModal(false);
            setGrnDetailsRows([]);
            setGrnDetailsProductKey('');
            return;
        }
        setGrnDetailsTitle(`List of GRN — ${productRow.product_name || 'Product'}`);
        setGrnDetailsRows((productRow.grn_details || []).map((item) => mapGrnDetailRow({
            ...item,
            isChecked: true,
        })));
    };

    const handleGrnDetailAction = (action, row) => {
        if (!row) {
            return;
        }
        if (action === 'select' || action === 'unselect') {
            const checked = action === 'select';
            setGrnDetailsRows((current) => current.map((item) => (
                String(item.id) === String(row.id)
                    ? mapGrnDetailRow({ ...item, isChecked: checked })
                    : item
            )));
            return;
        }
        if (action === 'remove') {
            Swal.fire({
                title: 'Remove this GRN?',
                text: row.grn_no || '',
                showCancelButton: true,
                confirmButtonText: 'Yes',
                cancelButtonText: 'No',
            }).then((result) => {
                if (!result.isConfirmed) {
                    return;
                }
                setSelectedGrns((current) => {
                    const nextSelected = current.filter((grn) => String(grn.id) !== String(row.grn_id));
                    const nextRows = buildRowsFromGrns(nextSelected);
                    setRowsData(nextRows);
                    refreshGrnDetailsModal(grnDetailsProductKey, nextRows);
                    return nextSelected;
                });
            });
        }
    };

    const closeGrnDetailsModal = () => {
        const uncheckedIds = new Set(
            (grnDetailsRows || [])
                .filter((row) => row.isChecked === false)
                .map((row) => String(row.grn_id)),
        );
        if (uncheckedIds.size) {
            setSelectedGrns((current) => {
                const nextSelected = current.filter((grn) => !uncheckedIds.has(String(grn.id)));
                setRowsData(buildRowsFromGrns(nextSelected));
                return nextSelected;
            });
        }
        setShowGrnDetailsModal(false);
        setGrnDetailsProductKey('');
    };

    const openGrnPicker = () => {
        if (!inputs.plant_id || !inputs.vendor_id) {
            Swal.fire('Please select Plant and Vendor first.', '', 'warning');
            return;
        }
        setShowGrnModal(true);
        setIsGrnLoading(true);
        getGrnsForPurchaseInvoice(cookies, inputs.plant_id, inputs.vendor_id)
            .then((response) => {
                setIsGrnLoading(false);
                const list = response.grn_list || [];
                if (!list.length) {
                    Swal.fire('No uninvoiced GRNs found for this vendor.', '', 'info');
                }
                setGrnPickerData(list.map((grn) => ({
                    ...grn,
                    order_qty: formatQty(grn.received_qty),
                    grade: (grn.detail_list || []).map((d) => d.product?.name).filter(Boolean).join(', '),
                })));
            })
            .catch((error) => {
                setIsGrnLoading(false);
                setShowGrnModal(false);
                displayError(error.response?.data ?? error.message, 'Failed to load GRNs');
            });
    };

    const onGrnMultiSelect = (rows) => {
        const selected = rows || [];
        setSelectedGrns(selected);
        setRowsData(buildRowsFromGrns(selected));
        setShowGrnModal(false);
    };

    const addManualRow = () => {
        const tax = taxes[0];
        const breakdown = calcTaxBreakdown(0, tax?.perc || 0, {
            tax_name: tax?.label?.split(' (')[0] || '',
        });
        setRowsData((current) => ([
            ...current,
            {
                key: `manual-${Date.now()}`,
                product_id: '',
                product_name: '',
                unit: '',
                dc_qty: 0,
                invoice_qty: '',
                basic_rate: '',
                taxable_amt: 0,
                tax_id: tax?.value || '',
                ...breakdown,
            },
        ]));
    };

    const updateManualRow = (index, field, value) => {
        setRowsData((current) => {
            const next = [...current];
            const row = { ...next[index], [field]: value };
            if (field === 'product_id') {
                const product = products.find((p) => String(p.value) === String(value));
                row.product_name = product?.label || '';
                row.unit = product?.unit || '';
            }
            const qty = Number(row.invoice_qty) || 0;
            const rate = Number(row.basic_rate) || 0;
            const taxable = Number((qty * rate).toFixed(2));
            const tax = taxes.find((t) => String(t.value) === String(row.tax_id));
            const breakdown = calcTaxBreakdown(taxable, tax?.perc || 0, {
                tax_name: tax?.label?.split(' (')[0] || '',
            });
            row.taxable_amt = taxable;
            row.dc_qty = qty;
            Object.assign(row, breakdown);
            next[index] = row;
            return next;
        });
    };

    const removeManualRow = (index) => {
        setRowsData((current) => current.filter((_, i) => i !== index));
    };

    const Cancel = () => {
        navigate('/PurchaseInvoiceTable');
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        if (!inputs.plant_id || !inputs.vendor_id || !inputs.invoice_date) {
            Swal.fire('Plant, Vendor and Invoice Date are required.', '', 'warning');
            return;
        }
        if (!inputs.invoice_no) {
            Swal.fire('Invoice number is not generated yet. Re-select Plant / Date and try again.', '', 'warning');
            return;
        }
        if (isWithGrn && !selectedGrns.length) {
            Swal.fire('Please select at least one GRN.', '', 'warning');
            return;
        }
        if (!rowsData.length) {
            Swal.fire('Please add invoice lines.', '', 'warning');
            return;
        }
        if (!isWithGrn) {
            const invalid = rowsData.some((row) => !row.product_id || !row.tax_id || !(Number(row.invoice_qty) > 0));
            if (invalid) {
                Swal.fire('Each line needs Product, Tax and Qty.', '', 'warning');
                return;
            }
        }

        Swal.fire({
            title: 'Do you want to save?',
            showCancelButton: true,
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
        }).then(async (result) => {
            if (!result.isConfirmed) {
                return;
            }
            setInProgress(true);
            const payload = {
                plant_id: parseInt(inputs.plant_id, 10),
                vendor_id: parseInt(inputs.vendor_id, 10),
                invoice_mode: inputs.invoice_mode,
                invoice_no: inputs.invoice_no,
                prefix: inputs.prefix,
                invoice_date: getDisplayDate(inputs.invoice_date),
                invoice_time: inputs.invoice_time || getCurrentTime(),
                period_from: getDisplayDate(inputs.invoice_date),
                period_to: getDisplayDate(inputs.invoice_date),
                freight_charges: Number(inputs.freight_charges) || 0,
                discount_amt: Number(inputs.discount_amt) || 0,
                tcs_amt: Number(inputs.tcs_amt) || 0,
                rounded_off: Number(totals.rounded_off),
                taxable_amt: Number(totals.taxable),
                gst_amt: Number(totals.gst),
                cgst_amt: Number(totals.cgst),
                sgst_amt: Number(totals.sgst),
                igst_amt: Number(totals.igst),
                gross_amt: Number(totals.gross),
                invoice_qty: Number(totals.qty),
                invoice_amt: Number(totals.invoice_amt),
                user_remarks: inputs.remarks || '',
                grn_ids: isWithGrn ? selectedGrns.map((g) => g.id) : [],
                detail_list: isWithGrn
                    ? rowsData.map((row) => ({
                        product_id: row.product_id,
                        tax_id: row.tax_id,
                        invoice_qty: row.invoice_qty,
                        basic_rate: row.basic_rate,
                        taxable_amt: row.taxable_amt,
                        gst_amt: row.gst_amt,
                        cgst_amt: row.cgst_amt,
                        sgst_amt: row.sgst_amt,
                        igst_amt: row.igst_amt,
                        po_detail_id: row.po_detail_id,
                    }))
                    : rowsData.map((row) => ({
                        product_id: parseInt(row.product_id, 10),
                        tax_id: parseInt(row.tax_id, 10),
                        invoice_qty: Number(row.invoice_qty),
                        basic_rate: Number(row.basic_rate),
                        taxable_amt: row.taxable_amt,
                        gst_amt: row.gst_amt,
                        cgst_amt: row.cgst_amt,
                        sgst_amt: row.sgst_amt,
                        igst_amt: row.igst_amt,
                    })),
            };

            try {
                const response = await createPurchaseInvoice(cookies, payload);
                const invoiceId = response?.data?.id;
                const pending = documents.filter((doc) => doc.isPending && doc.file);
                if (invoiceId && pending.length) {
                    try {
                        await uploadPendingPurchaseInvoiceDocuments(cookies, invoiceId, pending);
                    } catch (uploadError) {
                        setInProgress(false);
                        Swal.fire(
                            'Invoice saved',
                            'Some documents could not be uploaded. Open the invoice to attach them again.',
                            'warning',
                        ).then(() => navigate('/PurchaseInvoiceTable'));
                        return;
                    }
                }
                setInProgress(false);
                Swal.fire('Saved!', '', 'success').then(() => navigate('/PurchaseInvoiceTable'));
            } catch (error) {
                setInProgress(false);
                displayError(error.response?.data ?? error.message, 'Save Failed');
            }
        });
    };

    const grnColumns = useMemo(() => ([
        { fieldName: 'id', headerName: 'ROW_ACTIONS', size: 56 },
        { fieldName: 'goods_receipt_note_no', headerName: 'GRN No', size: 120 },
        { fieldName: 'goods_receipt_note_date', headerName: 'GRN Date', size: 100 },
        { fieldName: 'dc_no', headerName: 'DC No', size: 100 },
        { fieldName: 'grade', headerName: 'Product(s)', size: 180 },
        { fieldName: 'order_qty', headerName: 'Received Qty', size: 100, align: 'right' },
        { fieldName: 'vehicle_no', headerName: 'Vehicle', size: 110 },
    ]), []);

    const grnDetailsColumns = useMemo(() => ([
        { fieldName: 'id', headerName: 'ACTION_MENU', size: 110 },
        { fieldName: 'grn_no', headerName: 'GRN No', size: 160 },
        { fieldName: 'grn_date', headerName: 'GRN Date', size: 100 },
        { fieldName: 'dc_no', headerName: 'DC No', size: 100 },
        { fieldName: 'vehicle_no', headerName: 'Vehicle', size: 110 },
        { fieldName: 'unit', headerName: 'Unit', size: 70 },
        { fieldName: 'dc_qty_display', headerName: 'DC Qty', size: 90, align: 'right' },
        { fieldName: 'accepted_qty_display', headerName: 'Acc Qty', size: 90, align: 'right' },
        { fieldName: 'basic_rate_display', headerName: 'Rate', size: 90, align: 'right' },
        { fieldName: 'taxable_amt_display', headerName: 'Taxable', size: 110, align: 'right' },
    ]), []);

    return (
        <>
            <div id="outer-container" className="App">
                <Sidebar pageWrapId={'page-wrap'} outerContainerId={'outer-container'} />
                <ErrorToast />
                <LoadingOverlay inProgress={inProgress} />
                <div id="page-wrap">
                    <div className="premium-page-header-wrap">
                        <PremiumPageHeader
                            module="PURCHASE"
                            title="Purchase Invoice"
                            subtitle="Create invoice with GRN matching or without GRN, and upload supporting documents."
                            modeLabel="CREATE MODE"
                        />
                    </div>
                    <div className="list-form-page-shell">
                        <form onSubmit={handleSubmit}>
                            <FloatingControls
                                tableLink="/PurchaseInvoiceTable"
                                onCancel={Cancel}
                                enableCancel={true}
                                formLink="/PurchaseInvoice"
                                addNewLabel="New Purchase Invoice"
                            />

                            <div className="container form-control-panel">
                                <Row>
                                    <Col xl={6} lg={12} md={12}>
                                        <div className="form-row table-bordered shadow p-2 my-2 border-secondary form-control-panel">
                                            <label className="form-group col-sm-4 text-right">Invoice Mode</label>
                                            <select
                                                name="invoice_mode"
                                                className="form-control col-sm-7 mandatory-form-control"
                                                value={inputs.invoice_mode}
                                                onChange={handleChange}
                                            >
                                                <option value="with_grn">With GRN</option>
                                                <option value="without_grn">Without GRN</option>
                                            </select>

                                            <label className="form-group col-sm-4 text-right">Plant Location</label>
                                            <select
                                                name="plant_id"
                                                className="form-control col-sm-7 mandatory-form-control"
                                                required
                                                value={inputs.plant_id || ''}
                                                onChange={handleChange}
                                            >
                                                <option value="">Select plant</option>
                                                {plants.map((item) => (
                                                    <option key={item.value} value={item.value}>{item.label}</option>
                                                ))}
                                            </select>

                                            <label className="form-group col-sm-4 text-right">Vendor</label>
                                            <select
                                                name="vendor_id"
                                                className="form-control col-sm-7 mandatory-form-control"
                                                required
                                                value={inputs.vendor_id || ''}
                                                onChange={handleChange}
                                            >
                                                <option value="">Select vendor</option>
                                                {vendors.map((item) => (
                                                    <option key={item.value} value={item.value}>{item.label}</option>
                                                ))}
                                            </select>

                                            {isWithGrn && (
                                                <div className="w-100 d-flex align-items-center justify-content-between px-2 mt-2">
                                                    <span className="d-inline-flex align-items-center">
                                                        <label className="mb-0 mr-1">Select GRN</label>
                                                        <QuestionIcon onClickHandler={openGrnPicker} />
                                                    </span>
                                                    <span className="text-muted">
                                                        Selected: {selectedGrns.length}
                                                    </span>
                                                </div>
                                            )}

                                            <div className="w-100 d-flex align-items-center px-2 mt-2">
                                                <span className="d-inline-flex align-items-center">
                                                    <label className="mb-0 mr-1">Upload Centre</label>
                                                    <EditSharpIcon
                                                        onClick={() => setShowUploadCentre(true)}
                                                        sx={{
                                                            cursor: 'pointer',
                                                            color: documents.length ? '#1565c0' : '#d32f2f',
                                                        }}
                                                    />
                                                </span>
                                                <span className="ml-2 text-muted">
                                                    {documents.length} file(s)
                                                </span>
                                            </div>
                                        </div>
                                    </Col>

                                    <Col xl={6} lg={12} md={12}>
                                        <div className="form-row table-bordered shadow p-2 my-2 border-secondary form-control-panel">
                                            <label className="form-group col-sm-4 text-right">Invoice No</label>
                                            <input
                                                className="form-control col-sm-7"
                                                value={`${inputs.prefix || ''}${inputs.invoice_no || ''}`}
                                                disabled
                                                style={{ cursor: 'not-allowed' }}
                                            />
                                            <label className="form-group col-sm-4 text-right">Invoice Date</label>
                                            <input
                                                type="date"
                                                name="invoice_date"
                                                className="form-control col-sm-7 mandatory-form-control"
                                                required
                                                value={inputs.invoice_date || ''}
                                                onChange={handleChange}
                                            />
                                            <label className="form-group col-sm-4 text-right">Remarks</label>
                                            <input
                                                name="remarks"
                                                className="form-control col-sm-7"
                                                value={inputs.remarks || ''}
                                                onChange={handleChange}
                                            />
                                        </div>
                                    </Col>
                                </Row>
                            </div>

                            {rowsData.length > 0 && (
                                <div className="form-row shadow p-2 my-2 border-secondary p-2 mb-3 bg-blue container table-responsive">
                                    <div className="d-flex justify-content-between align-items-center w-100 px-1 pb-2">
                                        <h5 className="mb-0"><b>INVOICE LINES</b></h5>
                                        {!isWithGrn && (
                                            <Button type="button" color="primary" size="sm" onClick={addManualRow}>
                                                Add Line
                                            </Button>
                                        )}
                                    </div>
                                    <table
                                        className="table-bordered table-responsive searchTable table order-list"
                                        id="piLinesTable"
                                        style={{
                                            textAlign: 'center',
                                            borderRadius: '10px',
                                            backgroundColor: 'RGB(188,232,253)',
                                            border: '4px solid rgb(235, 73, 73)',
                                        }}
                                    >
                                        <thead>
                                            <tr style={{ backgroundColor: 'RGB(188,232,253)' }}>
                                                {!isWithGrn && <th>Action</th>}
                                                <th>Product Code</th>
                                                <th>Unit</th>
                                                <th>DC Qty</th>
                                                <th>Acc/Invoice Qty</th>
                                                <th>Basic Rate</th>
                                                <th>Taxable Amount</th>
                                                <th>GST %</th>
                                                <th>CGST Amount</th>
                                                <th>SGST Amount</th>
                                                <th>IGST Amount</th>
                                                <th>GST Amount</th>
                                                <th>Gross Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rowsData.map((row, index) => (
                                                <tr key={row.key || index}>
                                                    {!isWithGrn && (
                                                        <td>
                                                            <Button type="button" color="danger" size="sm" onClick={() => removeManualRow(index)}>
                                                                X
                                                            </Button>
                                                        </td>
                                                    )}
                                                    <td>
                                                        {isWithGrn ? (
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                name="product_name"
                                                                style={{ width: '100px', cursor: 'pointer' }}
                                                                value={row.product_name || ''}
                                                                readOnly
                                                                title="Click to view GRN list"
                                                                onClick={() => showGrnDetails(row)}
                                                            />
                                                        ) : (
                                                            <select
                                                                className="form-control form-control-sm"
                                                                value={row.product_id || ''}
                                                                onChange={(e) => updateManualRow(index, 'product_id', e.target.value)}
                                                            >
                                                                <option value="">Select</option>
                                                                {products.map((p) => (
                                                                    <option key={p.value} value={p.value}>{p.label}</option>
                                                                ))}
                                                            </select>
                                                        )}
                                                    </td>
                                                    <td>{row.unit || ''}</td>
                                                    <td>{formatQty(row.dc_qty || row.invoice_qty || 0)}</td>
                                                    <td>
                                                        {isWithGrn ? formatQty(row.invoice_qty) : (
                                                            <input
                                                                type="number"
                                                                step="any"
                                                                className="form-control form-control-sm text-right"
                                                                value={row.invoice_qty}
                                                                onChange={(e) => updateManualRow(index, 'invoice_qty', e.target.value)}
                                                            />
                                                        )}
                                                    </td>
                                                    <td style={{ textAlign: 'right' }}>
                                                        {isWithGrn ? formatAmount(row.basic_rate) : (
                                                            <input
                                                                type="number"
                                                                step="any"
                                                                className="form-control form-control-sm text-right"
                                                                value={row.basic_rate}
                                                                onChange={(e) => updateManualRow(index, 'basic_rate', e.target.value)}
                                                            />
                                                        )}
                                                    </td>
                                                    <td style={{ textAlign: 'right' }}>{formatAmount(row.taxable_amt)}</td>
                                                    <td style={{ whiteSpace: 'nowrap' }}>
                                                        {isWithGrn ? (row.tax_label || `GST @ ${row.tax_perc || 0}%`) : (
                                                            <select
                                                                className="form-control form-control-sm"
                                                                value={row.tax_id || ''}
                                                                onChange={(e) => updateManualRow(index, 'tax_id', e.target.value)}
                                                            >
                                                                <option value="">Select</option>
                                                                {taxes.map((t) => (
                                                                    <option key={t.value} value={t.value}>{t.label}</option>
                                                                ))}
                                                            </select>
                                                        )}
                                                    </td>
                                                    <td style={{ textAlign: 'right' }}>{formatAmount(row.cgst_amt)}</td>
                                                    <td style={{ textAlign: 'right' }}>{formatAmount(row.sgst_amt)}</td>
                                                    <td style={{ textAlign: 'right' }}>{formatAmount(row.igst_amt)}</td>
                                                    <td style={{ textAlign: 'right' }}>{formatAmount(row.gst_amt)}</td>
                                                    <td style={{ textAlign: 'right' }}>{formatAmount(row.gross_amt)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {rowsData.length === 0 && (
                                <div className="form-row shadow p-2 my-2 border-secondary p-2 mb-3 bg-blue container">
                                    <div className="d-flex justify-content-between align-items-center w-100 px-1">
                                        <h5 className="mb-0"><b>INVOICE LINES</b></h5>
                                        {!isWithGrn && (
                                            <Button type="button" color="primary" size="sm" onClick={addManualRow}>
                                                Add Line
                                            </Button>
                                        )}
                                    </div>
                                    <p className="text-center text-muted mb-0 w-100 py-3">
                                        {isWithGrn
                                            ? 'Select GRNs to load matching invoice lines.'
                                            : 'Click Add Line to enter products.'}
                                    </p>
                                </div>
                            )}

                            <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 bg-blue container table-responsive">
                                <table
                                    className="table order-list"
                                    id="piTotalsTable"
                                    style={{
                                        textAlign: 'center',
                                        borderRadius: '10px',
                                        backgroundColor: 'white',
                                        border: '4px solid rgb(235, 73, 73)',
                                    }}
                                >
                                    <tbody>
                                        <tr>
                                            <td colSpan="6" style={{ textAlign: 'right', backgroundColor: 'white' }}>
                                                <b>Total Taxable Amount </b>
                                            </td>
                                            <td colSpan="4">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    style={{ backgroundColor: 'white', cursor: 'not-allowed', textAlign: 'right' }}
                                                    value={formatAmount(totals.taxable)}
                                                    readOnly
                                                />
                                            </td>
                                            <td colSpan="6" style={{ textAlign: 'right', backgroundColor: 'white' }}>
                                                <b>CGST Amount </b>
                                            </td>
                                            <td colSpan="4">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    style={{ backgroundColor: 'white', cursor: 'not-allowed', textAlign: 'right' }}
                                                    value={formatAmount(totals.cgst)}
                                                    readOnly
                                                />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td colSpan="6" style={{ textAlign: 'right', backgroundColor: 'white' }}>
                                                <b>Total GST Amount </b>
                                            </td>
                                            <td colSpan="4">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    style={{ backgroundColor: 'white', cursor: 'not-allowed', textAlign: 'right' }}
                                                    value={formatAmount(totals.gst)}
                                                    readOnly
                                                />
                                            </td>
                                            <td colSpan="6" style={{ textAlign: 'right', backgroundColor: 'white' }}>
                                                <b>SGST Amount </b>
                                            </td>
                                            <td colSpan="4">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    style={{ backgroundColor: 'white', cursor: 'not-allowed', textAlign: 'right' }}
                                                    value={formatAmount(totals.sgst)}
                                                    readOnly
                                                />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td colSpan="6" style={{ textAlign: 'right', backgroundColor: 'white' }}>
                                                <b>Total Gross Amount </b>
                                            </td>
                                            <td colSpan="4">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    style={{ backgroundColor: 'white', cursor: 'not-allowed', textAlign: 'right' }}
                                                    value={formatAmount(totals.gross)}
                                                    readOnly
                                                />
                                            </td>
                                            <td colSpan="6" style={{ textAlign: 'right', backgroundColor: 'white' }}>
                                                <b>IGST Amount </b>
                                            </td>
                                            <td colSpan="4">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    style={{ backgroundColor: 'white', cursor: 'not-allowed', textAlign: 'right' }}
                                                    value={formatAmount(totals.igst)}
                                                    readOnly
                                                />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td colSpan="6" style={{ textAlign: 'right', backgroundColor: 'white' }}>
                                                <b>Freight </b>
                                            </td>
                                            <td colSpan="4">
                                                <input
                                                    type="number"
                                                    step="any"
                                                    name="freight_charges"
                                                    className="form-control"
                                                    style={{ backgroundColor: 'white', textAlign: 'right' }}
                                                    value={inputs.freight_charges}
                                                    onChange={handleChange}
                                                />
                                            </td>
                                            <td colSpan="6" style={{ textAlign: 'right', backgroundColor: 'white' }}>
                                                <b>Discount </b>
                                            </td>
                                            <td colSpan="4">
                                                <input
                                                    type="number"
                                                    step="any"
                                                    name="discount_amt"
                                                    className="form-control"
                                                    style={{ backgroundColor: 'white', textAlign: 'right' }}
                                                    value={inputs.discount_amt}
                                                    onChange={handleChange}
                                                />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td colSpan="6" style={{ textAlign: 'right', backgroundColor: 'white' }}>
                                                <b>Round off </b>
                                            </td>
                                            <td colSpan="4">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    style={{ backgroundColor: 'white', cursor: 'not-allowed', textAlign: 'right' }}
                                                    value={formatAmount(totals.rounded_off)}
                                                    readOnly
                                                />
                                            </td>
                                            <td colSpan="6" style={{ textAlign: 'right', backgroundColor: 'white' }}>
                                                <b>TCS Amount </b>
                                            </td>
                                            <td colSpan="4">
                                                <input
                                                    type="number"
                                                    step="any"
                                                    name="tcs_amt"
                                                    className="form-control"
                                                    style={{ backgroundColor: 'white', textAlign: 'right' }}
                                                    value={inputs.tcs_amt}
                                                    onChange={handleChange}
                                                />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td colSpan="14" style={{ textAlign: 'right', backgroundColor: 'white' }}>
                                                <b>Total Invoice Amount </b>
                                            </td>
                                            <td colSpan="4">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    style={{ backgroundColor: 'white', cursor: 'not-allowed', textAlign: 'right', fontWeight: 700 }}
                                                    value={formatAmount(totals.invoice_amt)}
                                                    readOnly
                                                />
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <div className="text-center pb-2">
                                <Button type="submit" color="success">Save Purchase Invoice</Button>
                            </div>
                        </form>
                    </div>

                    <div className="modal" tabIndex="-1" style={{ display: showGrnModal ? 'block' : 'none' }}>
                        <div className="modal-dialog modal-xl so-picker-modal-dialog">
                            <div className="modal-content">
                                <div className="modal-body">
                                    <div className="container item-list-table-container so-picker-table-wrap">
                                        <ReactTablePopUpSubmitWrapper
                                            title="Select GRNs"
                                            columns={grnColumns}
                                            data={grnPickerData}
                                            isLoading={isGrnLoading}
                                            onClose={() => setShowGrnModal(false)}
                                            onRowSubmit={() => {}}
                                            enableMultiSelect={true}
                                            onMultiSubmit={onGrnMultiSelect}
                                            initialPageSize={15}
                                            compactRows={false}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="modal" tabIndex="-1" style={{ display: showGrnDetailsModal ? 'block' : 'none' }}>
                        <div className="modal-dialog modal-xl so-picker-modal-dialog">
                            <div className="modal-content">
                                <div className="modal-body">
                                    <div className="container item-list-table-container so-picker-table-wrap">
                                        <ReactTablePopUpSubmitWrapper
                                            title={grnDetailsTitle}
                                            columns={grnDetailsColumns}
                                            data={grnDetailsRows}
                                            isLoading={false}
                                            onClose={closeGrnDetailsModal}
                                            onRowSubmit={() => {}}
                                            onRowAction={handleGrnDetailAction}
                                            initialPageSize={15}
                                            compactRows={false}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <PiUploadCentreModal
                        isOpen={showUploadCentre}
                        toggle={() => setShowUploadCentre(false)}
                        cookies={cookies}
                        documents={documents}
                        onDocumentsChange={setDocuments}
                    />
                </div>
            </div>
        </>
    );
}

export default PurchaseInvoice;
